/**
 * For fiscal sponsors: how a sponsored project appears, why its identity sits
 * under the sponsor's number, what the site shows of it and where, what
 * claiming a listing will mean once accounts exist, and how to correct what
 * is shown today. Written for the sponsor who is the identity authority in
 * this model and the reader most likely to care that a project is right.
 */
import { COPY } from '../../copy.js';
import type { DocPage } from '../types.js';

export const sponsorsPage: DocPage = {
  slug: 'sponsors',
  title: 'Sponsored projects',
  audience: 'fiscal sponsors',
  promise: 'How a project appears under its sponsor, why the sponsor is its identity authority, what is shown and where, and how to correct it.',
  lead: 'A fiscal sponsor files one return for many projects. FunderGraph shows each project inside its sponsor’s context, under an identifier scoped to the sponsor’s EIN, and treats the sponsor as the authority on what the project is.',
  sections: [
    {
      id: 'appears',
      heading: 'How a project appears',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Two rules make a project, both from the filing’s own words. A Schedule I row whose recipient number is the filer’s own number is a project the filer hosts: the organization reported a grant to itself on a project’s behalf. A Schedule I row with no recipient number, on a return whose program text calls the filer a fiscal sponsor, is a project it sponsors. Every other name without a number is a recipient to resolve, not a project; a community foundation that lists grantees without numbers is not their sponsor.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'A project found this way is named under its sponsor: its identifier is the sponsor’s EIN followed by the project’s name, so two sponsors hosting projects of the same name are two projects, and one sponsor naming one project in three returns is one project with three citations. The sponsor’s page lists its projects under "Sponsored projects" with the number of grant rows that name each, and each grant to a project is shown on the sponsor’s page in the project’s context, with the return it came from.',
          ],
        },
      ],
    },
    {
      id: 'authority',
      heading: 'Why the sponsor is the authority',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A project has no return of its own, so nothing in the public record identifies it except what its sponsor files. If FunderGraph minted an identity for every project name it saw, a funder’s spelling would make a second project, a move between sponsors would make a third, and none would have a filing to stand on. Scoping the project under the sponsor keeps its identity where its evidence is. The same rule gives the sponsor the standing to say what the project is called, which sponsor it belongs to, and when it has become an organization of its own with a number, at which point it has a page and the rule no longer applies.',
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'This is the listing rule applied to projects: an organization that files its own return has its own page, and a group that appears only inside another organization’s filing is shown only in that filing’s context. ',
            { link: '/about/reading#listing', text: 'The reading page states the rule' },
            '; downstream products may apply stricter ones, and the graph itself takes no other position.',
          ],
        },
      ],
    },
    {
      id: 'claiming',
      heading: 'Claiming a listing',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Today no project has a page of its own. When accounts exist, a sponsor will be able to claim its projects: confirm the name, say which funders’ rows belong to which project, and ask for a project to be shown with a listing of its own under the sponsor’s name. Until then the site shows what the filings say, and the sponsor is the one who can say what they should have said.',
          ],
        },
      ],
    },
    {
      id: 'corrections',
      heading: 'Correcting what is shown',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A project that is missing, misnamed, assigned to the wrong sponsor, or shown with a grant that belongs to another project is a correction the sponsor is best placed to make. Write to ',
            { link: `mailto:${COPY.site.contact}`, text: COPY.site.contact },
            ' with the sponsor’s EIN, the project’s name as filed, the return and the grant rows in question, and what should be shown instead. A correction that the filings do not support is recorded as a note on the project rather than a change to the facts, because the facts are what was filed.',
          ],
        },
      ],
    },
  ],
};
